import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  CircularProgress,
  Chip,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { cancelBooking, HistoryApi } from "../../../services/apiService";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";

import socket from "../../../utils/socket";

export default function HistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(null);

  // 2. Real-time Listener

  useEffect(() => {
    socket.on("bookingUpdated", (data) => {
      Historydata(false);
    });

    // Cleanup on unmount
    return () => {
      socket.off("connect");
      socket.off("bookingUpdated");
    };
  }, []);

  const Historydata = async (isLogin = true) => {
    try {
      setLoading(isLogin);
      const systemId = localStorage.getItem("systemId");
      const data = await HistoryApi(systemId);

      const rawData = data.data ? data.data : [];

      const sortedData = [...rawData].sort((a, b) => {
        const timeA = a?.createdAt?._seconds || 0;
        const timeB = b?.createdAt?._seconds || 0;
        return timeB - timeA;
      });

      setHistory(sortedData);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Historydata();
  }, []);

  const handleCancel = async (id, subId) => {
    try {
      setCancelLoading(subId);

      await cancelBooking({
        id,
        subId,
        status: "Canceled",
      });

      setHistory((prev) =>
        prev.map((booking) =>
          booking.id === id
            ? {
                ...booking,
                services: booking.services.map((service) =>
                  service.id === subId
                    ? { ...service, status: "Canceled" }
                    : service,
                ),
              }
            : booking,
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setCancelLoading(null);
    }
  };

  // status based change
  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    switch (s) {
      case "booked":
        return "#008000"; // Green (Chip se match)
      case "confirm":
        return "#FFD700"; // Yellow (Chip se match)
      case "completed":
        return "#008000"; // Green (Chip se match)

      case "canceled":
        return "#d32f2f"; // Dark Red
      case "pending":
        return "#ff9800"; //Orange

      default:
        return "#1a1a1a";
    }
  };
  // navigate to home page
  const gotoHomepage = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        py: { xs: 4, md: 8 },
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            p: { xs: 2, sm: 4 },
            borderRadius: 4,
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "800", mb: 4, color: "#1a1a1a" }}
          >
            My Bookings History
          </Typography>
          <Button
            onClick={gotoHomepage}
            startIcon={<ArrowBackIcon />}
            sx={{
              fontWeight: "800",
              backgroundColor: "#3f876d",
              color: "white",
              textTransform: "none",
              marginBottom: "30px",
              textAlign: "center",
              borderRadius: "8px",
              fontSize: { xs: "0.75rem", sm: "0.9rem" },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1 },
              "&:hover": {
                backgroundColor: "#2e6b56",
              },
            }}
          >
            BACK TO HOME
          </Button>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <CircularProgress size={50} sx={{ color: "#000" }} />
              <Typography sx={{ mt: 2, fontWeight: 500 }}>
                Loading your services...
              </Typography>
            </Box>
          ) : history.length === 0 ? (
            <Typography sx={{ opacity: 0.6, textAlign: "center", py: 10 }}>
              No bookings found.
            </Typography>
          ) : (
            history.map((data) => {
              const totalServicesCount = data?.services?.length || 0;

              return (
                <Box key={data?.id} sx={{ mb: 6 }}>
                  {/* --- Highlighted Date Header with Total Count --- */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 3,
                      background: "#1a1a1a",
                      color: "#fff",
                      p: 1.5,
                      px: 2,
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  >
                    {/* DATE */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CalendarMonthIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "700",
                          letterSpacing: 1,
                          fontSize: { xs: "0.8rem", sm: "1rem" },
                        }}
                      >
                        DATE: {data?.schedule?.date}
                      </Typography>
                    </Box>

                    {/* SERVICES CHIP */}
                    <Chip
                      label={`${totalServicesCount} ${
                        totalServicesCount > 1 ? "Services" : "Service"
                      }`}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        color: "#fff",
                        fontWeight: "700",
                        fontSize: "0.75rem",
                        order: { xs: 2, sm: 3 },
                      }}
                    />
                  </Box>
                  {/* MESSAGE */}
                  <Typography
                    component="div"
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      color: getStatusColor(data?.services[0]?.status),
                      fontWeight: "bold",
                      mt: "-10px",
                      width: "100%",
                      fontSize: { xs: "0.85rem", sm: "1rem" },
                    }}
                  >
                    <Marquee
                      direction="left"
                      speed={100}
                      pauseOnHover
                      gradient={false}
                      style={{ width: "100%" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          justifyContent: "center",
                          alignItems: "center",
                          pr: "120px",
                        }}
                      >
                        {data?.message}
                      </Box>
                    </Marquee>
                  </Typography>

                  {/* Services Loop */}
                  {data.services.map((service, sIndex) => {
                    return (
                      <Card
                        key={`${service.id}-${sIndex}`}
                        sx={{
                          mb: 2,
                          borderRadius: 3,
                          overflow: "hidden",
                          transition: "0.3s",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                          },
                          border: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <CardContent sx={{ p: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                            }}
                          >
                            <Box
                              component="img"
                              src={service?.image}
                              sx={{
                                width: { xs: "100%", sm: 140 },
                                height: 140,
                                objectFit: "cover",
                              }}
                            />

                            <Box sx={{ p: 2, flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 800, color: "#2c3e50" }}
                                >
                                  {service?.title}
                                </Typography>
                              </Box>

                              <Grid
                                container
                                spacing={{ xs: 3, sm: 10 }}
                                sx={{ mt: 1 }}
                              >
                                <Grid item xs={4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    PRICE
                                  </Typography>
                                  <Typography variant="body2" fontWeight="700">
                                    ₹{service?.price}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    QTY
                                  </Typography>
                                  <Typography variant="body2" fontWeight="700">
                                    x{service?.quantity || 1}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    TOTAL
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="800"
                                    color="primary.main"
                                  >
                                    ₹
                                    {(service?.price || 0) *
                                      (service?.quantity || 1)}
                                  </Typography>
                                </Grid>
                                {/* Status Column */}
                                <Grid item xs={6} sm={2.4}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    STATUS
                                  </Typography>
                                  <Chip
                                    label={service?.status}
                                    size="small"
                                    sx={{
                                      fontWeight: "800",
                                      fontSize: "0.65rem",
                                      borderRadius: "6px",
                                      textTransform: "capitalize",

                                      backgroundColor:
                                        {
                                          booked: "#008000",
                                          confirm: "#FFD700",
                                          completed: "#008000",
                                          canceled: "#ffebee",
                                          pending: "#ff9800",
                                        }[service?.status?.toLowerCase()] || "",

                                      color:
                                        {
                                          booked: "#FFFFFF",
                                          confirm: "rgba(0, 0, 0, 0.8)",
                                          complete: "#FFFFFF",
                                          pending: "#ff9800",
                                          canceled: "#d32f2f",
                                        }[service?.status?.toLowerCase()] || "",
                                    }}
                                  />
                                </Grid>
                                {/* Cancel Button Column */}
                                {service?.status?.toLowerCase() ===
                                  "booked" && (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={2.4}
                                    sx={{
                                      textAlign: { xs: "left", sm: "right" },
                                    }}
                                  >
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        handleCancel(data.id, service.id)
                                      }
                                      color="error"
                                      size="small"
                                      disabled={cancelLoading === service.id}
                                    >
                                      {cancelLoading === service.id
                                        ? "Cancelling..."
                                        : "Cancel"}
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              );
            })
          )}
        </Box>
      </Container>
    </Box>
  );
}
